/**
 * SlideCanvas — Constrained 16:9 viewport with shape rendering
 * Shapes are absolutely positioned HTML divs
 * Supports multi-select (Shift+click or Ctrl+click)
 */

import { useState, useRef, useCallback } from 'react';
import SlideShape from './SlideShape';

const SLIDE_W = 960;
const SLIDE_H = 540;

export default function SlideCanvas({ shapes, selectedId, selectedIds, onSelect, onUpdate, onDelete, onAdd, bg, showSlideNumbers, slideNumber }) {
  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [dragOver, setDragOver] = useState(false);

  // Compute scale to fit container
  const updateScale = useCallback((el) => {
    if (!el) return;
    containerRef.current = el;
    const rect = el.getBoundingClientRect();
    const s = Math.min(rect.width / SLIDE_W, rect.height / SLIDE_H);
    setScale(s);
  }, []);

  const handleCanvasClick = (e) => {
    if (e.target === e.currentTarget || e.target.dataset.canvas) {
      onSelect(null, false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Delete' || e.key === 'Backspace') {
      if (selectedId && document.activeElement?.tagName !== 'TEXTAREA' && document.activeElement?.tagName !== 'INPUT') {
        e.preventDefault();
        onDelete(selectedId);
      }
    }
  };

  // SlideShape calls onUpdate(fullShapeObject) — bridge to PresentationEditor's onUpdate(id, updates)
  const handleShapeUpdate = useCallback((fullShape) => {
    if (!fullShape?.id) return;
    onUpdate(fullShape.id, fullShape);
  }, [onUpdate]);

  // Drag and drop images onto canvas
  const handleDrop = async (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = [...(e.dataTransfer?.files || [])].filter(f => f.type.startsWith('image/'));
    if (files.length === 0) return;

    // Calculate drop position relative to canvas
    const rect = containerRef.current?.getBoundingClientRect();
    const dropX = rect ? (e.clientX - rect.left) / scale : SLIDE_W / 2 - 150;
    const dropY = rect ? (e.clientY - rect.top) / scale : SLIDE_H / 2 - 100;

    for (const file of files) {
      try {
        const form = new FormData();
        form.append('file', file);
        const res = await fetch('/api/work/documents/upload-image', { method: 'POST', credentials: 'include', body: form });
        const data = await res.json();
        if (data.storageKey) {
          onAdd({
            id: `shape_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
            type: 'image',
            x: Math.max(0, Math.min(dropX - 150, SLIDE_W - 300)),
            y: Math.max(0, Math.min(dropY - 100, SLIDE_H - 200)),
            width: 300, height: 200,
            storageKey: data.storageKey,
            fill: 'transparent',
            stroke: '', strokeWidth: 0, radius: 0,
            text: '', textColor: '#333', fontSize: 14, fontWeight: 'normal', textAlign: 'center',
            opacity: 1, zIndex: Date.now(),
          });
        }
      } catch (err) {
        console.error('Image drop upload failed:', err);
      }
    }
  };

  const effectiveSelectedIds = selectedIds || (selectedId ? new Set([selectedId]) : new Set());

  return (
    <div ref={updateScale}
      className={`flex-1 flex items-center justify-center bg-gray-200 overflow-hidden p-4 transition-colors ${dragOver ? 'bg-blue-100' : ''}`}
      onClick={handleCanvasClick} onKeyDown={handleKeyDown} tabIndex={0}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}>
      <div className="relative shadow-2xl rounded-lg overflow-hidden"
        data-canvas="true"
        style={{
          width: SLIDE_W * scale,
          height: SLIDE_H * scale,
          backgroundColor: bg || '#ffffff',
        }}>
        <div style={{ width: SLIDE_W, height: SLIDE_H, transform: `scale(${scale})`, transformOrigin: 'top left', overflow: 'hidden', position: 'relative' }}>
          {shapes.map(shape => (
            <SlideShape
              key={shape.id}
              shape={shape}
              selected={effectiveSelectedIds.has(shape.id)}
              scale={scale}
              onSelect={(id, multi) => onSelect(id, multi)}
              onUpdate={handleShapeUpdate}
              onDelete={onDelete}
            />
          ))}
          {shapes.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center text-gray-300 text-lg select-none pointer-events-none">
              Click a shape from the toolbar to add it
            </div>
          )}

          {/* Slide number badge — bottom-right corner */}
          {showSlideNumbers && slideNumber != null && (
            <div
              className="absolute bottom-0 right-0 pointer-events-none select-none"
              style={{
                padding: '4px 10px',
                fontSize: 13,
                fontWeight: 500,
                color: 'rgba(0,0,0,0.35)',
                lineHeight: 1,
                letterSpacing: '0.01em',
              }}
            >
              {slideNumber}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export { SLIDE_W, SLIDE_H };
